import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Cookie } from "lucide-react";

const SECTIONS = [
  {
    title: "1. Titolare del Trattamento",
    body: [
      "Il titolare del trattamento dei dati personali è HELPLY SRLS, con sede in Via Gabriele Caiulo 17–19, 73100 Lecce (LE), Italia.",
      "Puoi contattarci in qualsiasi momento per questioni relative alla privacy: email HELPLYSRLS.LE@GMAIL.COM — telefono 375 539 8236.",
    ],
  },
  {
    title: "2. Dati che Raccogliamo",
    body: [
      "Tramite il modulo \"Richiedi un Preventivo\" raccogliamo: indirizzo email (obbligatorio), numero di telefono (facoltativo), servizio desiderato e messaggio libero (facoltativi).",
      "Raccogliamo inoltre dati tecnici di navigazione (indirizzo IP, tipo di browser, sistema operativo) necessari al funzionamento e alla sicurezza del sito.",
      "Non raccogliamo categorie particolari di dati personali e non effettuiamo profilazione automatizzata.",
    ],
  },
  {
    title: "3. Finalità e Base Giuridica",
    body: [
      "I dati forniti vengono utilizzati esclusivamente per rispondere alla tua richiesta di preventivo, ricontattarti telefonicamente o via email e gestire l'eventuale rapporto contrattuale.",
      "La base giuridica è l'esecuzione di misure precontrattuali richieste dall'interessato (art. 6, par. 1, lett. b del GDPR) e l'adempimento di obblighi di legge (art. 6, par. 1, lett. c).",
    ],
  },
  {
    title: "4. Conservazione dei Dati",
    body: [
      "Conserviamo i dati delle richieste di preventivo per un massimo di 24 mesi dalla richiesta, salvo l'instaurarsi di un rapporto contrattuale o diversi obblighi di legge.",
      "Decorso tale periodo, i dati vengono cancellati in modo sicuro.",
    ],
  },
  {
    title: "5. Comunicazione a Terzi",
    body: [
      "I tuoi dati non vengono mai venduti né ceduti a terzi per finalità di marketing.",
      "Possono essere trattati da fornitori tecnici (hosting, servizio di invio email) che operano come responsabili del trattamento nel rispetto del GDPR.",
    ],
  },
  {
    title: "6. I Tuoi Diritti",
    body: [
      "Ai sensi degli articoli 15–22 del GDPR hai diritto di: accedere ai tuoi dati, chiederne la rettifica o la cancellazione, limitare o opporti al trattamento, e richiedere la portabilità dei dati.",
      "Per esercitare i tuoi diritti scrivi a HELPLYSRLS.LE@GMAIL.COM. Hai inoltre diritto di proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).",
    ],
  },
];

const COOKIE_POINTS = [
  "Questo sito utilizza esclusivamente cookie tecnici, necessari al corretto funzionamento delle pagine (ad esempio preferenze di sessione e sicurezza).",
  "Non utilizziamo cookie di profilazione, cookie pubblicitari né strumenti di tracciamento di terze parti.",
  "Poiché sono presenti solo cookie tecnici, non è richiesto il consenso preventivo tramite banner cookie (art. 122 del Codice Privacy e Linee guida del Garante).",
  "Puoi comunque cancellare o bloccare i cookie dalle impostazioni del tuo browser in qualsiasi momento.",
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-void font-sans text-[#16345F] antialiased" data-testid="privacy-page">
      <header className="sticky top-0 z-50 border-b border-[#D9E4F5] bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="privacy-logo">
            <img
              src="/logo.png"
              alt="Logo HELPLY SRLS"
              className="h-10 w-10 rounded-full border border-[#D9E4F5] bg-white object-cover"
            />
            <span className="font-display text-sm font-bold tracking-[0.2em] text-[#16345F]">
              HELPLY<span className="ml-1.5 text-[10px] text-neon">SRLS</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-button"
            className="group flex items-center gap-1.5 rounded-full border border-[#C9D8EE] px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[#16345F] transition-all duration-300 hover:border-neon hover:text-neon"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" />
            Torna al sito
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-16 sm:px-8 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-neon">
            {"// Documento Legale"}
          </p>
          <h1 className="mt-4 font-display text-3xl font-black leading-tight tracking-tight sm:text-4xl">
            Privacy <span className="text-pulse">&amp;</span> Cookie Policy
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
            Informativa sul trattamento dei dati personali ai sensi del Regolamento (UE) 2016/679
            ("GDPR") e del D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018.
            Ultimo aggiornamento: luglio 2026.
          </p>
        </motion.div>

        <div className="mt-12 space-y-6">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={s.title}
              data-testid={`privacy-section-${i + 1}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl border border-[#D9E4F5] bg-white p-6 shadow-[0_8px_32px_rgba(22,52,95,0.06)] sm:p-8"
            >
              <h2 className="flex items-center gap-3 font-display text-lg font-semibold">
                <ShieldCheck className="h-5 w-5 shrink-0 text-neon" />
                {s.title}
              </h2>
              <div className="mt-4 space-y-3">
                {s.body.map((p) => (
                  <p key={p.slice(0, 30)} className="text-sm leading-relaxed text-slate-600 sm:text-base">
                    {p}
                  </p>
                ))}
              </div>
            </motion.section>
          ))}

          <motion.section
            data-testid="privacy-cookie-section"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl border border-neon/30 bg-white p-6 shadow-[0_8px_32px_rgba(53,101,174,0.10)] sm:p-8"
          >
            <h2 className="flex items-center gap-3 font-display text-lg font-semibold">
              <Cookie className="h-5 w-5 shrink-0 text-pulse" />
              7. Cookie Policy
            </h2>
            <ul className="mt-4 space-y-3">
              {COOKIE_POINTS.map((p) => (
                <li key={p.slice(0, 30)} className="flex gap-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-pulse" />
                  {p}
                </li>
              ))}
            </ul>
          </motion.section>
        </div>

        <p className="mt-10 text-center font-mono text-xs text-slate-500">
          Per qualsiasi dubbio: <a href="mailto:HELPLYSRLS.LE@GMAIL.COM" className="text-neon underline">HELPLYSRLS.LE@GMAIL.COM</a> — 375 539 8236
        </p>
      </main>
    </div>
  );
}
